import { Dispatch } from '@reduxjs/toolkit';
import { notification } from 'antd';
import clientReqSlice from './clientReqSlice';
import {
  ITableFilters,
  ICheckedAurReqs,
  IClientReqToBeAdded,
  INewClientReqs,
  IRegulatoryReq,
  IClientReq,
  IApplicability,
  ICategory
} from './types';
import ApiService from 'utils/ApiCalls';
import { LanguageList } from '../globalTypes';
import Helpers from 'utils/Helpers';
import { applyFiltersToData } from 'utils/hooks/useFilterMapping';
import { RootState } from 'store/configureStore';

const {
  set_applied_filters_result,
  set_merged_elements,
  set_copied_elements,
  set_client_reqs,
  set_new_client_reqs,
  set_checked_elements_toBe_merged,
  set_checked_elements_toBe_copied,
  set_checked_aur_reqs,
  set_table_filters,
  set_filtered_data,
  set_is_app_statuses,
  update_client_req,
  reset_new_client_reqs,
  remove_client_req,
  set_updated,
  set_loading,
  clear_loading
} = clientReqSlice.actions;

const setAppliedFilersResult =
  (data: IRegulatoryReq[]) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_applied_filters_result(data));
    } catch (error) {
      console.error(error);
    }
  };

const setCheckedElementsToBeMerged =
  (elements: number[]) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_checked_elements_toBe_merged(elements));
    } catch (error) {
      console.error(error);
    }
  };

const setCheckedElementsToBeCopied =
  (elements: number[]) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_checked_elements_toBe_copied(elements));
    } catch (error) {
      console.error(error);
    }
  };

const setMergedElements = (elements: number[]) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_merged_elements(elements));
  } catch (error) {
    console.error(error);
  }
};

const setCopiedElements = (elements: number[]) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_copied_elements(elements));
  } catch (error) {
    console.error(error);
  }
};

const setFilteredData =
  (filteredData: IRegulatoryReq[]) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_filtered_data(filteredData));
    } catch (error) {
      console.error(error);
    }
  };

const setCheckedAurReqs = (checkedAurReqs: number) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_checked_aur_reqs(checkedAurReqs));
  } catch (error) {
    console.error(error);
  }
};

const setClientReqs =
  (clientReqData: INewClientReqs) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_client_reqs(clientReqData));
    } catch (error) {
      console.error(error);
    }
  };

const setNewClientReqs =
  (updatedAurReqs: IClientReqToBeAdded[]) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());

      updatedAurReqs.map((newClientReq: IClientReqToBeAdded) =>
        dispatch(set_new_client_reqs(newClientReq))
      );
    } catch (error) {
      console.error(error);
    }
  };

const setTableFilter = (filter: ITableFilters) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_table_filters(filter));
  } catch (error) {
    console.error(error);
  }
};

const setApplicabilityStatuses =
  (data: ICheckedAurReqs) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_is_app_statuses(data));
    } catch (error) {
      console.error(error);
    }
  };

const updateClientReq =
  (value: IClientReq, clnReqKey: string, aurReqId: number | string) =>
  (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(update_client_req({ value, clnReqKey, aurReqId }));
    } catch (error) {
      console.error(error);
    }
  };

const deleteClientReq =
  (bru_id: number | string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(set_loading());

      // Check if this key only exists in updated_client_reqs (unsaved/new row)
      const state = getState();
      const clientReqs = state.clientReq.clientReqs;
      let isUnsaved = false;

      for (const aurReqId of Object.keys(clientReqs)) {
        const aurReq = clientReqs[aurReqId];
        const inUpdated = aurReq.updated_client_reqs?.[bru_id] !== undefined;
        const inSaved = aurReq.client_reqs?.[bru_id] !== undefined;
        if (inUpdated && !inSaved) {
          isUnsaved = true;
          break;
        }
      }

      if (isUnsaved) {
        dispatch(remove_client_req({ clnReqKey: String(bru_id) }));
        dispatch(clear_loading());
        return;
      }

      const response = await ApiService.deleteClnReq(bru_id);

      if (response) {
        dispatch(remove_client_req({ clnReqKey: String(bru_id) }));
        dispatch(set_updated(true));

        notification.success({
          message: 'Success',
          description: 'Client Requirement successfully deleted',
          duration: 3
        });
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete client requirement';

      notification.error({
        message: 'Error',
        description: backendMessage,
        duration: 3
      });
    } finally {
      dispatch(clear_loading());
    }
  };

const saveClientReqs =
  (data: IClientReq[], lang: LanguageList, skipTransformation = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(set_loading());

      let transformedData;

      if (skipTransformation) {
        transformedData = data;
      } else {
        const state = getState();
        const orgContext = state.global.hierarchicalLevelsScope;
        const filters = Helpers.toApiFilters(orgContext);

        transformedData = applyFiltersToData(data, filters, {
          generateBruId: true
        });
      }

      const response = await ApiService.addClientReq(transformedData, lang);

      dispatch(set_updated(true));
      dispatch(reset_new_client_reqs());

      notification.success({
        message: response.status,
        description: 'Client Requirements successfully saved',
        duration: 20
      });

      return response;
    } catch (error: any) {
      console.error('Error in saveClientReqs operation:', error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to save client requirements';

      notification.error({
        message: 'Error',
        description: errorMessage,
        duration: 20
      });
      throw error;
    } finally {
      dispatch(clear_loading());
    }
  };

const updateApplicability =
  (data: IApplicability[], lang: LanguageList) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());

      const response = await ApiService.changeApplicability(data, lang);

      dispatch(set_updated(true));

      notification.success({
        message: response.status,
        description: response.data.message,
        duration: 20
      });
    } catch (error) {
      console.error(error);
    }
  };

const updateCategory =
  (data: ICategory[], lang: LanguageList) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());

      const response = await ApiService.changeCategory(data, lang);

      dispatch(set_updated(true));

      notification.success({
        message: response.status,
        description: response.data.message || 'Category updated successfully',
        duration: 20
      });
    } catch (error) {
      console.error('Category update failed:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update category',
        duration: 20
      });
    }
  };

const resetUpdated = () => (dispatch: Dispatch) => {
  dispatch(set_updated(false));
};

const setUpdated = (updated: boolean) => (dispatch: Dispatch) => {
  dispatch(set_updated(updated));
};

export default {
  setAppliedFilersResult,
  setCheckedElementsToBeMerged,
  setCheckedElementsToBeCopied,
  setTableFilter,
  setFilteredData,
  setApplicabilityStatuses,
  deleteClientReq,
  setClientReqs,
  setNewClientReqs,
  setMergedElements,
  setCopiedElements,
  setCheckedAurReqs,
  updateClientReq,
  saveClientReqs,
  updateApplicability,
  updateCategory,
  resetUpdated,
  setUpdated
};
